/**
 * resolveDefaultPaymentMethod — Phase 3 (Task P3-4), Entscheidung D
 *
 * Reiner, getesteter Vorauswahl-Resolver für die Bezahlart einer Buchung. Kapselt
 * die in Entscheidung D festgelegte Fallback-Kette und wird von der Admin-
 * `PaymentMethodTable`-Vorauswahl (P3-3) sowie den Buchungs-Summary-Komponenten
 * (P3-5/P3-6) konsumiert.
 *
 * Drei Kurs-Modi (`courseMode`):
 *   - "mandatory" (Nur-Online-Kurs): IMMER Online.
 *   - "optional"  (Kombi-Kurs): gültiger Teilnehmer-Default; sonst Online.
 *   - "off"       (Klassik): gültiger Teilnehmer-Default; ist der Default Online
 *                 (im Klassik nicht erlaubt) oder seine Art nicht freigegeben ⇒
 *                 erste aktivierte eigene Methode in der verbindlichen Reihenfolge
 *                 SEPA-Lastschrift → Überweisung → Bar → ESR (ADR 0013; dieselbe
 *                 Reihenfolge wie der Server-Downgrade-Fallback).
 *                 Hinweis: Die Buchungs-Summaries rufen den Resolver derzeit nur
 *                 für "optional" auf; im Klassik-Kurs belegen sie direkt den
 *                 Teilnehmer-Default vor. Die Reihenfolge wirkt dort nur über
 *                 `pickFirstEnabledOwnMethod` (Mitarbeiter-Sperre).
 *
 * "Gültig" = der Teilnehmer-Default existiert, ist KEINE Online-Methode und seine
 * Methoden-Art ist beim Tenant aktiviert (eigene Methode ist tatsächlich
 * auswählbar). Die Quelle des Defaults ist `attendee.defaultPaymentMethod`
 * (NICHT der Booker — Entscheidung D).
 *
 * Pur: kein React, kein GraphQL, kein Apollo. Synchron. Node 14 / TS 4.7 kompatibel
 * (kein `satisfies`, kein `??=`, kein `arr.at`).
 */

import {getPaymentMethodType, PaymentMethodData, PaymentMethodType} from "./getPaymentMethodType";

/** Kurs-Modus, dreiwertig (spiegelt `Course.allowMolliePayment` + `Course.mollieOptional`). */
export type CourseMollieMode = "off" | "mandatory" | "optional";

/**
 * Welche eigenen (nicht-Online-) Zahlungsarten der Tenant aktiviert hat. Spiegelt
 * `GetAppSettingsQuery["settings"]` (`bankTransfer`/`sepaDebit`/`cash`/`esr`).
 * Online-Zahlung ist KEIN Tenant-Setting, sondern hängt am Mollie-Feature/Kurs.
 */
export type EnabledOwnMethods = {
    bankTransfer?: boolean | null;
    sepaDebit?: boolean | null;
    cash?: boolean | null;
    esr?: boolean | null;
};

/**
 * Auflösungsergebnis als diskriminiertes Union, damit die Caller eindeutig handeln
 * können (Online-Sentinel vorauswählen vs. eine konkrete eigene Methode behalten).
 *
 *   - {kind: "online"}                         ⇒ Online-Zahlung vorauswählen
 *     (Sentinel-Pseudo oder existierende Online-Methode — entscheidet der Caller).
 *   - {kind: "method", paymentMethodType, ...} ⇒ konkrete eigene Methode vorauswählen.
 *   - {kind: "none"}                            ⇒ keine sinnvolle Vorauswahl möglich.
 */
export type ResolvedDefaultPaymentMethod =
    | {kind: "online"}
    | {kind: "method"; paymentMethodType: PaymentMethodType; paymentMethodId?: string}
    | {kind: "none"};

/**
 * Verbindliche Fallback-Reihenfolge eigener Methoden (ADR 0013):
 * SEPA-Lastschrift → Überweisung → Bar → ESR. Ein vorhandenes Mandat ist die
 * stärkste Willenserklärung des Buchers; der Server-Downgrade-Fallback
 * (`mollieFeatureDowngradeFallback.ts`) nutzt dieselbe Reihenfolge.
 */
export const CLASSIC_FALLBACK_ORDER: ReadonlyArray<{type: PaymentMethodType; flag: keyof EnabledOwnMethods}> = [
    {type: "sepaDebit", flag: "sepaDebit"},
    {type: "bankTransfer", flag: "bankTransfer"},
    {type: "cash", flag: "cash"},
    {type: "esr", flag: "esr"},
];

/** Ist die Methoden-Art eine eigene (nicht-Online) Methode, die der Tenant aktiviert hat? */
export const isOwnTypeEnabled = (type: PaymentMethodType, enabled: EnabledOwnMethods): boolean => {
    switch (type) {
        case "bankTransfer":
            return enabled.bankTransfer === true;
        case "sepaDebit":
            // SEPA-Lastschrift (eigene Daten am PaymentMethod), Tenant-Flag `sepaDebit`.
            return enabled.sepaDebit === true;
        case "cash":
            return enabled.cash === true;
        case "esr":
            return enabled.esr === true;
        // creditCard/payPal sind Bucher-Methoden ohne eigenes Tenant-Flag — im
        // Klassik-Kontext (eigene Abwicklung) nicht Teil der Fallback-Kette.
        // onlinePayment/none werden vom Caller separat behandelt.
        default:
            return false;
    }
};

/**
 * Ist die konkrete Methode für Mitarbeiter wählbar? Eigene Arten nur, wenn beim
 * Tenant freigegeben; Online/Kreditkarte/PayPal haben kein Tenant-Flag und gelten
 * als wählbar (werden vom Caller gesondert behandelt).
 */
export const isPaymentMethodEnabledForTenant = (paymentMethod: PaymentMethodData | null | undefined, enabled: EnabledOwnMethods): boolean => {
    const type = getPaymentMethodType(paymentMethod);
    switch (type) {
        case "bankTransfer":
        case "sepaDebit":
        case "cash":
        case "esr":
            return isOwnTypeEnabled(type, enabled);
        default:
            return true;
    }
};

/**
 * Erste vorhandene Bucher-Methode einer freigegebenen eigenen Art in der
 * verbindlichen Reihenfolge, oder undefined. Grundlage der Vorauswahl, wenn der
 * Teilnehmer-Standard gesperrt ist (ADR 0013).
 */
export const pickFirstEnabledOwnMethod = <T extends PaymentMethodData>(methods: ReadonlyArray<T>, enabled: EnabledOwnMethods): T | undefined => {
    for (let i = 0; i < CLASSIC_FALLBACK_ORDER.length; i++) {
        const candidate = CLASSIC_FALLBACK_ORDER[i];
        if (enabled[candidate.flag] !== true) continue;
        const match = methods.find((pm) => getPaymentMethodType(pm) === candidate.type);
        if (match) return match;
    }
    return undefined;
};

/**
 * Liefert die erste beim Tenant aktivierte eigene Methode der verbindlichen
 * Reihenfolge (SEPA-Lastschrift → Überweisung → Bar → ESR) oder `none`.
 */
const firstEnabledOwnMethod = (enabled: EnabledOwnMethods): ResolvedDefaultPaymentMethod => {
    for (let i = 0; i < CLASSIC_FALLBACK_ORDER.length; i++) {
        const candidate = CLASSIC_FALLBACK_ORDER[i];
        if (enabled[candidate.flag] === true) {
            return {kind: "method", paymentMethodType: candidate.type};
        }
    }
    return {kind: "none"};
};

/**
 * Löst die Vorauswahl der Bezahlart gemäß Entscheidung D auf.
 *
 * @param attendeeDefault Der Teilnehmer-Default (`attendee.defaultPaymentMethod`) oder null.
 * @param enabledOwnMethods Beim Tenant aktivierte eigene Methoden.
 * @param courseMode Kurs-Modus (off/mandatory/optional).
 */
export const resolveDefaultPaymentMethod = (
    attendeeDefault: (PaymentMethodData & {id?: string}) | null | undefined,
    enabledOwnMethods: EnabledOwnMethods,
    courseMode: CourseMollieMode,
): ResolvedDefaultPaymentMethod => {
    // Nur-Online-Kurs: Online ist Pflicht, kein eigener Default greift.
    if (courseMode === "mandatory") {
        return {kind: "online"};
    }

    const defaultType = getPaymentMethodType(attendeeDefault);
    const defaultIsOnline = defaultType === "onlinePayment";
    const defaultId = attendeeDefault && attendeeDefault.id ? attendeeDefault.id : undefined;

    // Ist der Teilnehmer-Default eine gültige eigene Methode (nicht Online + beim
    // Tenant aktiviert)? Gilt für optional UND off.
    const defaultIsValidOwnMethod = !defaultIsOnline && isOwnTypeEnabled(defaultType, enabledOwnMethods);

    if (courseMode === "optional") {
        // Kombi-Kurs: gültiger eigener Default bleibt, sonst Online.
        if (defaultIsValidOwnMethod) {
            return {kind: "method", paymentMethodType: defaultType, paymentMethodId: defaultId};
        }
        return {kind: "online"};
    }

    // courseMode === "off" (Klassik): eigener Default bleibt, sonst Klassik-Fallback.
    if (defaultIsValidOwnMethod) {
        return {kind: "method", paymentMethodType: defaultType, paymentMethodId: defaultId};
    }

    // Default ist Online (im Klassik nicht erlaubt) ODER keine/ungültige eigene
    // Methode ⇒ erste aktivierte eigene Methode der verbindlichen Reihenfolge.
    return firstEnabledOwnMethod(enabledOwnMethods);
};
